import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';

import { BillingScreenLayout } from './billing-screen-layout.component';
import { CancelWithConfirm } from './cancel-with-confirm.component';
import { formatLongDate, formatNumber, formatShortDate } from './format';
import { SummaryRow } from './summary-row.component';

import {
  Company,
  PlanPurchase,
  cancelActivePlan,
  extractBackendErrorMessage,
  postActivePlan,
} from '@/services/hsm-backend';

export interface PlanFormProps {
  company: Company;
  refetch: () => Promise<void>;
}

interface PlanFormValues {
  messageQty: number | '';
  updateRenewalDate: boolean;
  renewalDate: string;
}

/**
 * "Contratar plan" screen. Left card shows the current plan (read-only
 * snapshot from the company); right card hosts the form that POSTs a
 * new active plan. On success the snackbar shows a confirmation, the
 * shell refetches the company and the form resets to the new state.
 *
 * @param company Loaded company from the shell's outlet context.
 * @param refetch Callback that retriggers the shell's getCompany.
 */
export const PlanForm = ({ company, refetch }: PlanFormProps) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const activePlan = company.subscription?.activePlan;
  const nextRenewal = company.subscription?.nextRenewalDate?.slice(0, 10) ?? '';

  const {
    register,
    handleSubmit,
    watch,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<PlanFormValues>({
    defaultValues: {
      messageQty: activePlan?.messageQty ?? '',
      updateRenewalDate: false,
      renewalDate: nextRenewal,
    },
  });

  const updateRenewalDate = watch('updateRenewalDate');

  const onSubmit = async (values: PlanFormValues) => {
    try {
      await postActivePlan(company.companyId, {
        messageQty: Number(values.messageQty),
        renewalDate: values.renewalDate,
        updateRenewalDate: values.updateRenewalDate,
      });
      enqueueSnackbar('Plan contratado', { variant: 'success' });
      await refetch();
      reset({
        messageQty: Number(values.messageQty),
        updateRenewalDate: values.updateRenewalDate,
        renewalDate: values.renewalDate,
      });
    } catch (error) {
      enqueueSnackbar(
        `No se pudo contratar el plan: ${extractBackendErrorMessage(error)}`,
        { variant: 'error' }
      );
    }
  };

  return (
    <BillingScreenLayout
      title="Contratar plan"
      subtitle="Establecé la cantidad de mensajes incluidos por ciclo y la fecha de renovación."
      currentCard={
        <CurrentPlanCard
          plan={activePlan}
          nextRenewalDate={company.subscription?.nextRenewalDate}
          planPurchases={company.subscription?.planPurchases}
          companyId={company.companyId}
          refetch={refetch}
        />
      }
      formCard={
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Box
              component="form"
              onSubmit={(e) => void handleSubmit(onSubmit)(e)}
              noValidate
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
                <Typography variant="h6">Nuevo plan</Typography>
                <Tooltip title="Contratar un plan nuevo reemplaza al actual de inmediato. El consumo del ciclo no se reinicia: se sigue contando desde la última fecha de renovación, así que los mensajes ya enviados se descuentan del nuevo plan.">
                  <IconButton
                    size="small"
                    sx={{ color: 'text.secondary' }}
                    aria-label="Cómo impacta contratar un plan nuevo"
                  >
                    <InfoOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Divider sx={{ mt: 1, mb: 2 }} />
              <Stack spacing={2}>
                <TextField
                  label="Cantidad de mensajes incluidos"
                  type="number"
                  fullWidth
                  inputProps={{ min: 1 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        mensajes / mes
                      </InputAdornment>
                    ),
                  }}
                  helperText={
                    errors.messageQty?.message ??
                    'Cantidad de mensajes incluidos en cada ciclo de facturación.'
                  }
                  error={Boolean(errors.messageQty)}
                  {...register('messageQty', {
                    required: 'Requerido',
                    valueAsNumber: true,
                    min: { value: 1, message: 'Debe ser mayor a 0' },
                  })}
                />
                <Controller
                  control={control}
                  name="updateRenewalDate"
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="body2" color="text.secondary">
                          Actualizar fecha de renovación (opcional)
                        </Typography>
                      }
                    />
                  )}
                />
                <TextField
                  label="Fecha de renovación"
                  type="date"
                  fullWidth
                  disabled={!updateRenewalDate}
                  InputLabelProps={{ shrink: true }}
                  helperText={
                    errors.renewalDate?.message ??
                    'Próxima fecha en que se reinicia el contador del plan.'
                  }
                  error={Boolean(errors.renewalDate)}
                  {...register('renewalDate', {
                    validate: (value, formValues) => {
                      if (!formValues.updateRenewalDate) {
                        return true;
                      }
                      if (!value) {
                        return 'Requerido';
                      }
                      // Parse as a local date (not UTC midnight) and compare
                      // at day granularity so a date picked for "today" in
                      // AR (UTC-3) isn't flagged as past on load.
                      const picked = new Date(`${value}T00:00:00`);

                      if (Number.isNaN(picked.getTime())) {
                        return 'Fecha inválida';
                      }
                      const today = new Date();

                      today.setHours(0, 0, 0, 0);
                      if (picked < today) {
                        return 'No puede ser una fecha pasada';
                      }
                      return true;
                    },
                  })}
                />
              </Stack>
              <Stack
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={1}
                sx={{ mt: 3 }}
              >
                <Button
                  variant="text"
                  onClick={() => navigate(`/company/${company.companyId}`)}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Contratar plan
                </Button>
              </Stack>
            </Box>
          </CardContent>
        </Card>
      }
    />
  );
};

interface CurrentPlanCardProps {
  plan?: PlanPurchase;
  nextRenewalDate?: string;
  planPurchases?: PlanPurchase[];
  companyId: string;
  refetch: () => Promise<void>;
}

const CurrentPlanCard = ({
  plan,
  nextRenewalDate,
  planPurchases,
  companyId,
  refetch,
}: CurrentPlanCardProps) => {
  // History excludes the active plan and is shown most-recent first.
  const previousPlans = (planPurchases ?? [])
    .filter((p) => p.id !== plan?.id)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6">Plan actual</Typography>
          {plan && (
            <CancelWithConfirm
              buttonLabel="Cancelar plan"
              title="Cancelar plan activo"
              description="La empresa quedará sin plan activo. Podés contratar uno nuevo cuando quieras."
              confirmLabel="Cancelar plan"
              successMessage="Plan cancelado"
              errorMessagePrefix="No se pudo cancelar el plan"
              onConfirm={() => cancelActivePlan(companyId)}
              onConfirmed={refetch}
            />
          )}
        </Stack>
        <Divider sx={{ mt: 1, mb: 2 }} />
        {plan ? (
          <Stack spacing={1.5}>
            <SummaryRow
              label="Mensajes incluidos"
              value={`${formatNumber(plan.messageQty)} / mes`}
            />
            <SummaryRow
              label="Fecha de renovación"
              value={formatLongDate(nextRenewalDate)}
            />
            <SummaryRow
              label="Contratado el"
              value={formatLongDate(plan.createdAt)}
            />
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            La empresa no tiene un plan activo todavía.
          </Typography>
        )}
        {previousPlans.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mt: 3 }}>
              Planes anteriores
            </Typography>
            <List dense disablePadding>
              {previousPlans.map((previous) => (
                <ListItem key={previous.id} disableGutters>
                  <ListItemText
                    primary={`${formatNumber(previous.messageQty)} mensajes`}
                    secondary={`Contratado el ${formatShortDate(previous.createdAt)}`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </CardContent>
    </Card>
  );
};
